import React, { useContext, useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FirebaseContext } from "../contexts/FirebaseContext";
import TopBar from "../components/TopBar";
import MarkdownDescription from "../components/MarkdownDescription";
import { ReactComponent as BackIcon } from "../assets/icon_back.svg";

function NoteDetailScreen() {
  const { noteId } = useParams();
  const { tasks, projects, updateTask, deleteTask } = useContext(FirebaseContext);
  const navigate = useNavigate();
  const [note, setNote] = useState(null);
  const [noteName, setNoteName] = useState("");
  const [noteDescription, setNoteDescription] = useState("");
  const [selectedProjectId, setSelectedProjectId] = useState(null);

  // Refs to store timeout IDs for debouncing
  const nameTimeoutRef = useRef(null);
  const descriptionTimeoutRef = useRef(null);

  // Fetch the note from the tasks list using noteId
  useEffect(() => {
    if (noteId && tasks.length > 0) {
      const selectedNote = tasks.find((t) => String(t.id) === noteId);
      if (selectedNote && selectedNote.usedAsNote) {
        setNote(selectedNote);
        setNoteName(selectedNote.title || "");
        setNoteDescription(selectedNote.description || "");
        setSelectedProjectId(selectedNote.projectId ?? null);
      } else {
        console.log("No note found with the given noteId.");
      }
    }
  }, [noteId, tasks]);

  // Debounce function for updating name or description
  const debounceUpdateNote = (field, value, timeoutRef) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      console.log(`Updating ${field} in Firestore:`, value);
      updateTask(noteId, { [field]: value });
    }, 500); // 500ms debounce
  };

  // Handle name change
  const handleNameChange = (e) => {
    const newName = e.target.value;
    setNoteName(newName);
    debounceUpdateNote("title", newName, nameTimeoutRef);
  };

  // Handle description change
  const handleDescriptionChange = (e) => {
    const newDescription = e.target.value;
    setNoteDescription(newDescription);
    debounceUpdateNote("description", newDescription, descriptionTimeoutRef);
  };

  // Handle project change — immediate persist (no debounce; mirrors Android).
  const handleProjectChange = (e) => {
    const newProjectId = Number(e.target.value);
    setSelectedProjectId(newProjectId);
    updateTask(noteId, { projectId: newProjectId });
  };

  // Handle deleting the note
  const handleDeleteNote = async () => {
    if (window.confirm("Are you sure you want to delete this note?")) {
      await deleteTask(noteId); // Call deleteTask from FirebaseContext
      navigate("/"); // Navigate back to the home screen after deletion
    }
  };

  // Cleanup timeouts when the component unmounts
  useEffect(() => {
    return () => {
      if (nameTimeoutRef.current) {
        clearTimeout(nameTimeoutRef.current);
      }
      if (descriptionTimeoutRef.current) {
        clearTimeout(descriptionTimeoutRef.current);
      }
    };
  }, []);

  if (!note) {
    return <div>Loading note details...</div>;
  }

  return (
    // Reserve the fixed 50px top bar's height so the content starts below it
    // instead of sliding underneath.
    <div style={{ paddingTop: "50px" }}>
      <TopBar
        title={noteName}
        leftContainer={
          <button
            onClick={() => navigate(-1)}
            style={{ background: "none", border: "none", cursor: "pointer" }}
          >
            <BackIcon
              style={{
                width: "24px",
                height: "24px",
                filter:
                  "brightness(0) saturate(100%) invert(19%) sepia(7%) saturate(1119%) hue-rotate(180deg) brightness(90%) contrast(94%)",
              }}
            />
          </button>
        }
      />
      <div style={{ padding: "16px" }}>
        {/* Note Name Input */}
        <div>
          <label htmlFor="noteName">Note Name:</label>
          <input
            type="text"
            id="noteName"
            value={noteName}
            onChange={handleNameChange}
            style={{
              width: "100%",
              padding: "8px",
              margin: "8px 0",
              borderRadius: "4px",
              border: "1px solid var(--border)",
              backgroundColor: "var(--surface)",
              color: "var(--text)",
              boxSizing: "border-box",
            }}
          />
        </div>

        {/* Note Description: Markdown (read-only, default) ↔ Raw (editable) */}
        <MarkdownDescription
          label="Note Description:"
          value={noteDescription}
          onChange={handleDescriptionChange}
          minHeight={280}
        />

        {/* Project selector — mirrors Android: only shown when >1 project exists */}
        {projects.filter((p) => !p.projectArchived).length > 1 && (
          <div style={{ marginTop: "20px" }}>
            <label
              htmlFor="noteProject"
              style={{ fontSize: "12px", color: "var(--text-muted)", display: "block", marginBottom: "6px" }}
            >
              Project
            </label>
            <select
              id="noteProject"
              value={selectedProjectId ?? ""}
              onChange={handleProjectChange}
              style={{
                width: "100%",
                padding: "10px",
                borderRadius: "8px",
                border: "1px solid var(--border)",
                backgroundColor: "var(--surface)",
                color: "var(--text)",
                fontSize: "14px",
                boxSizing: "border-box",
                cursor: "pointer",
              }}
            >
              {projects
                .filter((p) => !p.projectArchived)
                .map((p) => (
                  <option key={p.id} value={Number(p.id)}>
                    {p.projectName || "Untitled"}
                  </option>
                ))}
            </select>
          </div>
        )}

        {/* Add Delete Button */}
        <button
          onClick={handleDeleteNote}
          style={{
            marginTop: "32px",
            backgroundColor: "#ff4d4d", // Red color for delete button
            color: "#fff",
            border: "none",
            padding: "12px 16px",
            borderRadius: "8px",
            cursor: "pointer",
            width: "100%", // Full width button
          }}
        >
          Delete Note
        </button>
      </div>
    </div>
  );
}

export default NoteDetailScreen;
